// frontend/src/pages/DashboardPage.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import echo from "../services/echo";
import api from "../services/api";
import { useAuth } from "../contexts/AuthContext"; // R03
import DashboardGrid from "../components/dashboard/DashboardGrid";
import AtendimentoPromotoriaModal from "../components/common/AtendimentoPromotoriaModal";
import AtendimentoDefensoriaModal from "../components/common/AtendimentoDefensoriaModal";
import AtendimentoSaudeModal from "../components/common/AtendimentoSaudeModal";
import AtendimentoEducacaoModal from "../components/common/AtendimentoEducacaoModal";
import AtendimentoEspecialistaModal from "../components/common/AtendimentoEspecialistaModal";
import MovimentacaoResumoModal from "../components/common/MovimentacaoResumoModal";
import PermissionDeniedAlert from "../components/common/PermissionDeniedAlert";
import "../assets/css/dashboard-theme.css";

const norm = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toUpperCase()
    .trim();

function todayYmd() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function isYmdDate(value) {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function toCadastroTime(value) {
  const raw = value?.created_at;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

function sortByCadastroAsc(list) {
  return [...list].sort((a, b) => {
    const diff = toCadastroTime(a) - toCadastroTime(b);
    if (diff !== 0) return diff;
    return (a?.id ?? 0) - (b?.id ?? 0);
  });
}

const MEIO_ENTRADA_ICON = {
  [norm("Apresentação Espontânea")]: "bi-person-walking",
  [norm("Flagrante")]: "bi-lightning-charge",
  [norm("Flagrante + MBA")]: "bi-lightning-charge",
  [norm("MBA")]: "bi-clipboard-data",
  [norm("CDP")]: "bi-building",
  [norm("Recambiamento")]: "bi-arrow-left-right", // R06
  [norm("OUTROS")]: "bi-three-dots",
  [norm("Sem Meio de Entrada")]: "bi-collection",
};

function getIconForEntrada(nome) {
  const k = norm(nome || "Sem Meio de Entrada");
  return MEIO_ENTRADA_ICON[k] || "bi-collection";
}

const ATENDIMENTO_KEYS = [
  "atendimento_promotoria",
  "atendimento_defensoria",
  "atendimento_saude",
  "atendimento_educacao",
  "atendimento_especialistas",
];

export default function DashboardPage() {
  const { user, isWeekend, hasWeekendAccess } = useAuth(); // R03
  const location = useLocation();
  const dashboardResetAt = location.state?.dashboardResetAt;

  // Base do período (todas as movs retornadas pela API no range)
  const [itemsBase, setItemsBase] = useState([]);
  // Itens filtrados por status (open/closed/all)
  const [items, setItems] = useState([]);

  const [lastUpdateInfo, setLastUpdateInfo] = useState({ id: null, source: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // R01: default Status = "open" e Data = hoje
  const [status, setStatus] = useState(() => {
    const contextualStatus = location.state?.dashboardContext?.status;
    return ["open", "closed", "all"].includes(contextualStatus)
      ? contextualStatus
      : "open";
  }); // open | closed | all
  const [date, setDate] = useState(() => {
    const contextualDate = location.state?.dashboardContext?.date;
    return isYmdDate(contextualDate) ? contextualDate : todayYmd();
  });

  // Data atual do servidor
  const [serverDate, setServerDate] = useState(null);

  // Busca local (nome do adolescente)
  const [q, setQ] = useState(() => {
    const contextualQuery = location.state?.dashboardContext?.q;
    return typeof contextualQuery === "string" ? contextualQuery : "";
  });

  const [dashboardProfileSetting, setDashboardProfileSetting] = useState({
    grupos_iniciam_fechados: false,
    adolescentes_iniciam_fechados: false,
    source: "fallback",
  });
  const [gruposExpandidos, setGruposExpandidos] = useState({});

  // Estado comum das modais
  const [movSelecionada, setMovSelecionada] = useState(null);
  const [modalPromotoriaOpen, setModalPromotoriaOpen] = useState(false);
  const [modalDefensoriaOpen, setModalDefensoriaOpen] = useState(false);
  const [modalSaudeOpen, setModalSaudeOpen] = useState(false);
  const [modalEducacaoOpen, setModalEducacaoOpen] = useState(false);
  const [modalEspecialistasOpen, setModalEspecialistasOpen] = useState(false);

  // Estado do resumo modal
  const [resumoMovimentacaoId, setResumoMovimentacaoId] = useState(null);

  // Tema claro/escuro (persiste durante a sessão)
  // Padrão: LIGHT (lightMode = true)
  const [lightMode, setLightMode] = useState(() => {
    const saved = sessionStorage.getItem('dashboardLightMode');
    if (saved === null) return true;
    return saved === 'true';
  });

  const dashboardTheme = lightMode ? "light" : "dark";

  useEffect(() => {
    const syncDashboardTheme = (event) => {
      const nextMode = event?.detail?.lightMode;
      if (typeof nextMode === "boolean") {
        setLightMode(nextMode);
        return;
      }

      const saved = sessionStorage.getItem("dashboardLightMode");
      setLightMode(saved !== "false");
    };

    window.addEventListener("dashboard-theme-changed", syncDashboardTheme);
    return () => {
      window.removeEventListener("dashboard-theme-changed", syncDashboardTheme);
    };
  }, []);

  useEffect(() => {
    const mainContent = document.querySelector(".main-content");
    const painelContainer = document.querySelector(".painel-container");

    if (!mainContent && !painelContainer) return;

    const scopedDashboardClassName = "dashboard-main-content";
    const lightClassName = "dashboard-main-content-light";
    const shellLightClassName = "dashboard-shell-theme-light";

    if (mainContent) mainContent.classList.add(scopedDashboardClassName);

    if (dashboardTheme === "light") {
      if (mainContent) mainContent.classList.add(lightClassName);
      if (painelContainer) painelContainer.classList.add(shellLightClassName);
    } else {
      if (mainContent) mainContent.classList.remove(lightClassName);
      if (painelContainer) painelContainer.classList.remove(shellLightClassName);
    }

    return () => {
      if (mainContent) {
        mainContent.classList.remove(lightClassName);
        mainContent.classList.remove(scopedDashboardClassName);
      }
      if (painelContainer) painelContainer.classList.remove(shellLightClassName);
    };
  }, [dashboardTheme]);

  async function loadMovs(custom = {}) {
    try {
      setLoading(true);

      const effectiveStatus = custom.status ?? status;
      const effectiveDate = custom.date ?? date;

      // Para KPIs corretas, sempre pedimos TODAS as movs do período (status=all)
      const params = {
        per_page: 50,
        date_start: effectiveDate,
        date_end: effectiveDate,
        status: "all",
      };

      const r = await api.get("/movimentacoes", { params });
      const base = sortByCadastroAsc(r.data?.data ?? []);

      setItemsBase(base);

      // Filtro de status feito no frontend
      const filtered = base.filter((m) => {
        if (effectiveStatus === "open") return !m.finalizada;
        if (effectiveStatus === "closed") return m.finalizada;
        return true; // all
      });
      setItems(filtered);
    } catch (e) {
      if (e?.kind === 'permission') {
        // Erro 403 esperado - usuário sem permissão movimentacoes.view
        console.info("[DASH] Acesso negado: usuário sem permissão movimentacoes.view");
        setError(e);
      } else {
        // Outros erros não esperados
        console.error("[DASH] load error", e);
      }
      setItemsBase([]);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  // Primeira carga
  useEffect(() => {
    // Buscar data do servidor E informações de feriado/weekend
    api.get('/server-info').then((r) => {
      const dataAtual = r.data?.data_atual;
      if (dataAtual) {
        setServerDate(new Date(dataAtual + 'T00:00:00').toLocaleDateString('pt-BR'));
      }
      
      // Armazena globalmente para que isWeekend() possa acessar
      window.__serverInfo = {
        is_feriado: r.data?.is_feriado || false,
        is_weekend: r.data?.is_weekend || false,
        is_bloqueado: r.data?.is_bloqueado || false,
      };
    }).catch((e) => {
      console.error('[DASH] Erro ao buscar data do servidor:', e);
    });
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
    loadMovs();
  }, []);

  // Recarrega ao alterar a data
  useEffect(() => {
    if (date) {
      loadMovs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // Reset explícito do menu lateral quando já está no dashboard.
  useEffect(() => {
    if (!dashboardResetAt) return;

    const defaultDate = todayYmd();
    setStatus("open");
    setQ("");

    if (date !== defaultDate) {
      setDate(defaultDate);
      return;
    }

    loadMovs({ status: "open", date: defaultDate });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardResetAt]);

  // Atualizações em tempo real (Reverb / Echo)
  useEffect(() => {
    const ch = echo.channel("movimentacoes");

    const handler = (payload) => {
      const mov = payload?.movimentacao;
      if (!mov) return;
      const id = mov.id;

      // Atualiza base (para KPIs)
      setItemsBase((currBase) => {
        const idx = currBase.findIndex((m) => m.id === id);
        if (idx === -1) {
          return sortByCadastroAsc([...currBase, mov]);
        }
        const clone = currBase.slice();
        clone[idx] = mov;
        return sortByCadastroAsc(clone);
      });

      // Atualiza lista filtrada + aplica remoção automática no status=open/closed
      setItems((curr) => {
        const oldMov = curr.find((m) => m.id === id);
        let updateSource = null;

        if (oldMov) {
          for (const key of ATENDIMENTO_KEYS) {
            if (oldMov[key]?.updated_at !== mov[key]?.updated_at) {
              updateSource = key;
              break;
            }
          }
          if (!updateSource && oldMov.updated_at !== mov.updated_at) {
            updateSource = "geral";
          }
        }

        setLastUpdateInfo({ id, source: updateSource });
        setTimeout(() => setLastUpdateInfo({ id: null, source: null }), 2600);

        const idx = curr.findIndex((m) => m.id === id);
        const movFinalizada = !!mov.finalizada;

        // Se o filtro for "Abertos" e a mov foi finalizada => some da tela
        if (status === "open" && movFinalizada) {
          if (idx === -1) return curr;
          const clone = curr.slice();
          clone.splice(idx, 1);
          return clone;
        }

        // Se o filtro for "Finalizados" e a mov voltou a ficar aberta => some da tela
        if (status === "closed" && !movFinalizada) {
          if (idx === -1) return curr;
          const clone = curr.slice();
          clone.splice(idx, 1);
          return clone;
        }

        // Caso contrário, adiciona/atualiza no array filtrado
        if (idx === -1) {
          return sortByCadastroAsc([...curr, mov]);
        }
        const clone = curr.slice();
        clone[idx] = mov;
        return sortByCadastroAsc(clone);
      });
    };

    ch.listen(".movimentacao.saved", handler);
    return () => {
      ch.stopListening(".movimentacao.saved", handler);
    };
  }, [status]);

  // KPIs calculadas sobre TODO o período
  const total = itemsBase.length;
  const abertas = useMemo(
    () => itemsBase.filter((m) => !m.finalizada).length,
    [itemsBase]
  );
  const finalizadas = total - abertas;

  // Busca local por nome
  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((m) =>
      (m?.adolescente?.nome_adolescente ?? "").toLowerCase().includes(term)
    );
  }, [items, q]);

  // Agrupamento por meio_entrada
  // Agrupamento por meio_entrada com ordenação por prioridade (vem do backend)
  const grupos = useMemo(() => {
    const map = new Map();
    for (const m of shown) {
      const nomeEntrada = (m?.meio_entrada?.nome || "Sem Meio de Entrada").toString();
      if (!map.has(nomeEntrada)) map.set(nomeEntrada, []);
      map.get(nomeEntrada).push(m);
    }
    
    const entries = Array.from(map.entries());
    entries.sort((a, b) => {
      const nomeA = a[0];
      const nomeB = b[0];
      
      // Pega a ordem do primeiro item de cada grupo (vem do meio_entrada.ordem)
      const ordemA = a[1][0]?.meio_entrada?.ordem ?? 999;
      const ordemB = b[1][0]?.meio_entrada?.ordem ?? 999;
      
      // Ordena por ordem crescente (menor número = maior prioridade)
      if (ordemA !== ordemB) {
        return ordemA - ordemB;
      }
      
      // Se tiverem mesma ordem, ordena alfabeticamente
      return nomeA.localeCompare(nomeB, "pt-BR");
    });
    
    return entries;
  }, [shown]);

  useEffect(() => {
    const perfilNome = user?.perfil?.nome;

    const fallback = (() => {
      const normalized = norm(perfilNome);
      const consultivos = [norm("Ministério Público"), norm("NAIJUD (TJDFT)")];

      if (consultivos.includes(normalized)) {
        return {
          grupos_iniciam_fechados: true,
          adolescentes_iniciam_fechados: true,
          source: "fallback",
        };
      }

      return {
        grupos_iniciam_fechados: false,
        adolescentes_iniciam_fechados: false,
        source: "fallback",
      };
    })();

    let mounted = true;

    const loadDashboardSetting = async () => {
      try {
        const response = await api.get("/dashboard-profile-settings/current");
        const data = response?.data?.data;

        if (!mounted || !data) return;

        setDashboardProfileSetting({
          grupos_iniciam_fechados: !!data.grupos_iniciam_fechados,
          adolescentes_iniciam_fechados: !!data.adolescentes_iniciam_fechados,
          source: data.source || "config",
        });
      } catch {
        if (!mounted) return;
        setDashboardProfileSetting(fallback);
      }
    };

    if (user?.perfil_id) {
      loadDashboardSetting();
    } else {
      setDashboardProfileSetting(fallback);
    }

    return () => {
      mounted = false;
    };
  }, [user?.perfil_id, user?.perfil?.nome]);

  useEffect(() => {
    setGruposExpandidos((prev) => {
      const next = { ...prev };

      for (const [entradaNome] of grupos) {
        if (typeof next[entradaNome] === "undefined") {
          next[entradaNome] = !dashboardProfileSetting.grupos_iniciam_fechados;
        }
      }

      return next;
    });
  }, [grupos, dashboardProfileSetting.grupos_iniciam_fechados]);

  const toggleGrupo = (entradaNome) => {
    setGruposExpandidos((prev) => ({
      ...prev,
      [entradaNome]: !prev[entradaNome],
    }));
  };

  // Gate: permissão negada (tem prioridade sobre loading)
  if (error?.kind === 'permission') {
    return (
      <PermissionDeniedAlert
        requiredPermission="movimentacoes.view"
        action="visualizar o dashboard de movimentações"
      />
    );
  }

  // Gate: loading (só mostra se não houver erro de permissão)
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`content-header dashboard-page dashboard-theme dashboard-theme--${dashboardTheme}`}>
      <div className="dashboard-page-head">
        <div>
          <h1 className="content-title">Painel</h1>
          <p className="content-subtitle">
            Movimentações em tempo real
            {serverDate && <> • Hoje são <strong className="dashboard-server-date">{serverDate}</strong></>}
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-main">
          <div className="toolbar-left d-flex flex-wrap gap-2 align-items-center">
          {/* Status (filtro em memória) */}
          <div className="btn-group" role="group" aria-label="Filtro de status">
            <input
              type="radio"
              className="btn-check"
              id="api-open"
              autoComplete="off"
              checked={status === "open"}
              onChange={() => {
                setStatus("open");
                loadMovs({ status: "open" });
              }}
            />
            <label className="btn btn-outline-success" htmlFor="api-open">
              <i className="bi bi-hourglass-split me-1" /> Abertos
            </label>

            <input
              type="radio"
              className="btn-check"
              id="api-closed"
              autoComplete="off"
              checked={status === "closed"}
              onChange={() => {
                setStatus("closed");
                loadMovs({ status: "closed" });
              }}
            />
            <label className="btn btn-outline-success" htmlFor="api-closed">
              <i className="bi bi-check2-circle me-1" /> Finalizados
            </label>

            <input
              type="radio"
              className="btn-check"
              id="api-all"
              autoComplete="off"
              checked={status === "all"}
              onChange={() => {
                setStatus("all");
                loadMovs({ status: "all" });
              }}
            />
            <label className="btn btn-outline-success" htmlFor="api-all">
              <i className="bi bi-stack me-1" /> Todos
            </label>
          </div>

          {/* Data (API) */}
          <div className="d-flex align-items-center gap-2 ms-2">
            <div className="d-flex align-items-center gap-1">
              <span className="text-secondary small">Data</span>
              <input
                type="date"
                className="form-control"
                style={{ width: 170 }}
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          </div>

          </div>

          <div className="kpis toolbar-kpis" aria-label="Totalizadores do dashboard">
            <div className="kpi">
              <span className="dashboard-kpi-label">Efetivo Total</span>
              <span className="dashboard-kpi-value">{total}</span>
            </div>
            <div className="kpi">
              <span className="dashboard-kpi-label">Em aberto</span>
              <span className="dashboard-kpi-value">{abertas}</span>
            </div>
            <div className="kpi">
              <span className="dashboard-kpi-label">Finalizadas</span>
              <span className="dashboard-kpi-value">{finalizadas}</span>
            </div>
          </div>

          <div className="toolbar-search d-flex align-items-center">
            <div className="input-group" style={{ minWidth: 260 }}>
              <span className="input-group-text">
                <i className="bi bi-search" />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por nome do adolescente…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-light">Carregando…</div>
      ) : (
        <>
          {grupos.map(([entradaNome, lista]) => {
            const icon = getIconForEntrada(entradaNome);
            const countLabel = `${lista.length} ${
              lista.length === 1 ? "movimentação" : "movimentações"
            }`;
            const grupoExpandido = gruposExpandidos[entradaNome] ?? !dashboardProfileSetting.grupos_iniciam_fechados;
            return (
              <section key={entradaNome}>
                <button
                  type="button"
                  className="categoria-header w-100 border-0 bg-transparent p-0"
                  onClick={() => toggleGrupo(entradaNome)}
                  aria-expanded={grupoExpandido}
                >
                  <div className="categoria-line" />
                  <div className="categoria-pill-center">
                    <span className="me-2" aria-hidden="true">{grupoExpandido ? "−" : "+"}</span>
                    <i className={`bi ${icon}`} />
                    {entradaNome}
                  </div>
                  <div className="categoria-meta">{countLabel}</div>
                </button>
                {grupoExpandido ? (
                  <DashboardGrid
                    items={lista}
                    lastUpdateInfo={lastUpdateInfo}
                    isWeekend={isWeekend()}
                    hasWeekendAccess={hasWeekendAccess()}
                    lightMode={lightMode}
                    adolescentesIniciamFechados={dashboardProfileSetting.adolescentes_iniciam_fechados}
                    onOpenResumo={(mov) => {
                      setResumoMovimentacaoId(mov.id);
                    }}
                    onOpenPromotoria={(mov) => {
                      setMovSelecionada(mov);
                      setModalPromotoriaOpen(true);
                    }}
                    onOpenDefensoria={(mov) => {
                      setMovSelecionada(mov);
                      setModalDefensoriaOpen(true);
                    }}
                    onOpenSaude={(mov) => {
                      setMovSelecionada(mov);
                      setModalSaudeOpen(true);
                    }}
                    onOpenEducacao={(mov) => {
                      setMovSelecionada(mov);
                      setModalEducacaoOpen(true);
                    }}
                    onOpenEspecialistas={(mov) => {
                      setMovSelecionada(mov);
                      setModalEspecialistasOpen(true);
                    }}
                  />
                ) : null}
              </section>
            );
          })}
        </>
      )}

      {/* Modais de Atendimento */}
      <AtendimentoPromotoriaModal
        isOpen={modalPromotoriaOpen}
        onClose={() => setModalPromotoriaOpen(false)}
        theme={dashboardTheme}
        atendimento={movSelecionada?.atendimento_promotoria || null}
        movimentacaoId={movSelecionada?.id}
        movimentacaoFinalizada={movSelecionada?.finalizada}
        adolescenteNome={movSelecionada?.adolescente?.nome_adolescente}
        onSaveSuccess={() => {
          // Atualização visual vem em tempo real via Echo (movimentacao.saved)
        }}
      />

      <AtendimentoDefensoriaModal
        isOpen={modalDefensoriaOpen}
        onClose={() => setModalDefensoriaOpen(false)}
        theme={dashboardTheme}
        atendimento={movSelecionada?.atendimento_defensoria || null}
        movimentacaoId={movSelecionada?.id}
        movimentacaoFinalizada={movSelecionada?.finalizada}
        adolescenteNome={movSelecionada?.adolescente?.nome_adolescente}
        onSaveSuccess={() => {
          // Atualização visual vem em tempo real via Echo (movimentacao.saved)
        }}
      />

      <AtendimentoSaudeModal
        isOpen={modalSaudeOpen}
        onClose={() => setModalSaudeOpen(false)}
        theme={dashboardTheme}
        atendimento={movSelecionada?.atendimento_saude || null}
        movimentacaoId={movSelecionada?.id}
        movimentacaoFinalizada={movSelecionada?.finalizada}
        adolescenteNome={movSelecionada?.adolescente?.nome_adolescente}
        isMba={Number(movSelecionada?.meio_entrada?.id) === 3}
        onSaveSuccess={() => {
          // Atualização visual vem em tempo real via Echo (movimentacao.saved)
        }}
      />

      <AtendimentoEducacaoModal
        isOpen={modalEducacaoOpen}
        onClose={() => setModalEducacaoOpen(false)}
        theme={dashboardTheme}
        atendimento={movSelecionada?.atendimento_educacao || null}
        movimentacaoId={movSelecionada?.id}
        movimentacaoFinalizada={movSelecionada?.finalizada}
        adolescenteNome={movSelecionada?.adolescente?.nome_adolescente}
        onSaveSuccess={() => {
          // Atualização visual vem em tempo real via Echo (movimentacao.saved)
        }}
      />

      <AtendimentoEspecialistaModal
        isOpen={modalEspecialistasOpen}
        onClose={() => setModalEspecialistasOpen(false)}
        theme={dashboardTheme}
        atendimento={movSelecionada?.atendimento_especialistas || null}
        movimentacaoId={movSelecionada?.id}
        movimentacaoFinalizada={movSelecionada?.finalizada}
        adolescenteNome={movSelecionada?.adolescente?.nome_adolescente}
        onSaveSuccess={() => {
          // Atualização visual vem em tempo real via Echo (movimentacao.saved)
        }}
      />

      {/* Modal de Resumo da Movimentação */}
      <MovimentacaoResumoModal
        isOpen={!!resumoMovimentacaoId}
        onClose={() => setResumoMovimentacaoId(null)}
        theme={dashboardTheme}
        movimentacaoId={resumoMovimentacaoId}
        onDeleteSuccess={() => {
          loadMovs();
        }}
      />
    </div>
  );
}
